/**
 * App use per institution (Sep 11 2026): the cells the dashboard publishes
 * from the app's analytics. Students active over a trailing window, their
 * sessions, and the apply clicks the live app records.
 *
 * Built on the suppression core, never changing it (`suppression.ts` is
 * contractual). The Worker builds its snapshot with this
 * (`src/worker/lib/amplitude-activity.ts` reads Amplitude) and the sample
 * builds the demo with it, so the two cannot drift apart, as
 * `event-figure.ts` is shared for the same reason.
 *
 * ── THE FLOOR ────────────────────────────────────────────────────────────
 *  - Active students: withheld under the floor, and when the claimed
 *    students it leaves out are fewer than ten.
 *  - Sessions per student and the average session length are ratios, not
 *    counts of people, so each carries its two counts for the unsafe-count
 *    guard, and publishes only when the active students behind it do.
 *  - Apply clicks: a count of events with the students behind it
 *    (`eventFigure`).
 *  - An institution the analytics hold no student for reads unavailable,
 *    never zero: a student without the institution on their analytics
 *    profile is invisible here, so an empty answer is not a measurement.
 */

import { count, rate, suppressed, unavailable, type Cell, type Rate } from "~/lib/suppression";
import { eventFigure, type EventFigureCells } from "~/lib/event-figure";

/** The trailing window every app-use figure covers: about a term. At 30 days no school clears the floor. */
export const ACTIVITY_WINDOW_DAYS = 90;

const DAY_MS = 86_400_000;

/** YYYYMMDD in UTC, the format the Amplitude Dashboard REST API takes. */
export function ymd(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10).replace(/-/g, "");
}

/** The last ACTIVITY_WINDOW_DAYS complete days, ending yesterday in UTC, so a part-day never reads as a drop. */
export function activityWindow(nowMs: number): { readonly start: string; readonly end: string } {
  const end = nowMs - DAY_MS;
  return { start: ymd(end - (ACTIVITY_WINDOW_DAYS - 1) * DAY_MS), end: ymd(end) };
}

/** What the analytics answered for one institution. Internal: never serialized. */
export interface OrgActivityRaw {
  readonly activeStudents: number;
  readonly applyStudents: number;
  readonly applications: number;
  /** Opportunity opens, and the students behind them (Sep 19 2026). Null when
   *  the read did not run or did not answer, which is not the same as none. */
  readonly opens: number | null;
  readonly openStudents: number | null;
  /** Null when not asked (under the floor) or not answered. */
  readonly sessionsPerStudent: number | null;
  readonly averageSessionSeconds: number | null;
}

/**
 * A per-student ratio: sessions per active student, or the average session
 * length. Not a count of people, so it always travels with the two counts it
 * is built on, and the unsafe-count guard checks both.
 */
export interface Ratio {
  readonly numerator: number;
  readonly denominator: number;
  readonly ratio: number;
}

export interface ActivityCells {
  readonly window: { readonly start: string; readonly end: string; readonly days: number };
  /** Students active in the window. Withheld under the floor, and when the claimed students it leaves out are fewer than ten. */
  readonly activeStudents: Cell<number>;
  readonly activeShare: Cell<Rate>;
  /** Published only when the active students behind them publish. */
  readonly sessionsPerStudent: Cell<Ratio>;
  readonly averageSessionSeconds: Cell<Ratio>;
  /** Apply clicks, as clicks and the students behind them. */
  readonly applications: EventFigureCells;
  /** Opportunities opened, as opens and the students behind them (Sep 19 2026). */
  readonly opportunitiesOpened: EventFigureCells;
}

/** Why an institution has no app figures when the analytics hold no student under its name. */
export const NOT_IN_ANALYTICS =
  "none of your students appears under your institution's name in the app's analytics yet";

/** The opens read did not run this hour, or did not answer. */
export const OPENS_NOT_READ = "the analytics source did not answer for opportunities opened";

/**
 * Zero opens is NOT published as a zero (Sep 19 2026).
 *
 * The app began recording an opportunity open on September 2 2026, and an open
 * reaches this figure only when the student's analytics profile names the
 * institution. So a zero today means "nothing has reached us under your name
 * yet", which a reader would take as "your students opened nothing" and act
 * on. It reads unavailable with that sentence until the first open lands, and
 * counts from there.
 */
export const OPENS_NONE_YET = "no opportunity opens have reached P3 under your institution's name yet";

/** The day the app began recording an opportunity open, as the notes say it. */
export const OPENS_COUNTED_FROM = "September 2, 2026";

export function activityCells(
  raw: OrgActivityRaw | undefined,
  window: { readonly start: string; readonly end: string },
  claimed: number,
  floor: number,
): ActivityCells {
  const span = { start: window.start, end: window.end, days: ACTIVITY_WINDOW_DAYS };
  if (!raw) {
    const missing = unavailable(NOT_IN_ANALYTICS);
    return {
      window: span,
      activeStudents: missing,
      activeShare: missing,
      sessionsPerStudent: missing,
      averageSessionSeconds: missing,
      applications: { total: missing, students: missing, share: missing },
      opportunitiesOpened: { total: missing, students: missing, share: missing },
    };
  }
  // The analytics see a student only when their profile names the
  // institution, so they cannot truthfully see more students than the
  // roster: a larger figure is a mismatch between the two sources, capped
  // rather than shown.
  const active = Math.min(raw.activeStudents, claimed);
  const activeShare = rate(active, claimed);
  const activeStudents: Cell<number> = activeShare.kind === "value" ? count(active) : activeShare;

  // A ratio's denominator is the active count, so a ratio publishes only
  // when that count does.
  const behind: Cell<never> | null =
    activeShare.kind !== "value"
      ? activeShare
      : active === 0
        ? unavailable("no active students in the window")
        : active < floor
          ? suppressed("below-threshold")
          : null;
  const sessions = raw.sessionsPerStudent;
  const totalSessions = sessions !== null ? Math.max(Math.round(sessions * active), active) : null;
  const sessionsPerStudent: Cell<Ratio> =
    behind ??
    (sessions === null || totalSessions === null
      ? unavailable("the analytics source did not answer for sessions")
      : { kind: "value", value: { numerator: totalSessions, denominator: active, ratio: sessions } });
  const seconds = raw.averageSessionSeconds;
  const averageSessionSeconds: Cell<Ratio> =
    behind ??
    (seconds === null || totalSessions === null
      ? unavailable("the analytics source did not answer for sessions")
      : {
          kind: "value",
          value: { numerator: Math.round(seconds * totalSessions), denominator: totalSessions, ratio: seconds },
        });

  return {
    window: span,
    activeStudents,
    activeShare,
    sessionsPerStudent,
    averageSessionSeconds,
    applications: eventFigure(raw.applications, Math.min(raw.applyStudents, claimed), claimed, floor),
    opportunitiesOpened: openedFigure(raw, claimed, floor),
  };
}

/**
 * Opportunities opened, or the sentence that says why there is no figure.
 * Three states, and only the third is a count: not read this hour, nothing
 * recorded under the institution's name yet, and opens with the students
 * behind them.
 */
function openedFigure(raw: OrgActivityRaw, claimed: number, floor: number): EventFigureCells {
  const flat = (reason: string): EventFigureCells => ({
    total: unavailable(reason),
    students: unavailable(reason),
    share: unavailable(reason),
  });
  if (raw.opens === null || raw.openStudents === null) return flat(OPENS_NOT_READ);
  if (raw.opens === 0 || raw.openStudents === 0) return flat(OPENS_NONE_YET);
  return eventFigure(raw.opens, Math.min(raw.openStudents, claimed), claimed, floor);
}
